import { createFileRoute } from "@tanstack/react-router";
import { alternateLinkList } from "@/lib/i18n/locale";
import { LegalPage, P, UL } from "@/components/marketing/LegalPage";

export const Route = createFileRoute("/legal/anti-spam")({
  head: () => ({
    meta: [
      { title: "Anti-Spam Policy — banana.cards" },
      { name: "description", content: "Rules for email conduct, prohibited content, consent, and enforcement for messages sent through Banana Cards." },
      { property: "og:title", content: "Anti-Spam Policy — banana.cards" },
      { property: "og:description", content: "Email conduct, consent, prohibited content and enforcement on Banana Cards." },
    ],
    links: alternateLinkList("/legal/anti-spam", "en"),
  }),
  component: AntiSpamPage,
});

export function AntiSpamPage() {
  return (
    <LegalPage
      eyebrow="Legal · Trust"
      title="Anti-Spam"
      italic="Policy."
      updated="January 2026"
      intro="Banana Cards gives you tools to email your contacts — welcome emails, follow-ups, campaigns, signatures, and automations. This policy explains the rules every sender must follow when using those tools, what content is prohibited, and how we enforce these rules to keep deliverability healthy for everyone on the platform."
      related={[
        { label: "Terms of Service", to: "/terms" },
        { label: "Privacy Policy", to: "/privacy" },
        { label: "Legal & Ethical Use Guidelines", to: "/legal-guidelines" },
      ]}
      sections={[
        {
          id: "scope",
          title: "1. Scope",
          body: (
            <P>
              This policy applies to every email, message, or notification sent through Banana Cards — including welcome emails, follow-up sequences, broadcast campaigns, transactional messages, email signatures, contract notifications, booking confirmations, review requests, and any automation that results in a message being delivered to a recipient on your behalf.
            </P>
          ),
        },
        {
          id: "consent",
          title: "2. Consent and Permission",
          body: (
            <>
              <P>You may only send messages to recipients who have given you permission to contact them. Acceptable bases for sending include:</P>
              <UL items={[
                "the recipient explicitly opted in (form, checkbox, double opt-in)",
                "the recipient handed you their card at a networking event, meeting or exhibition",
                "the recipient is an existing customer or active business contact under a legitimate-interest basis permitted by local law",
                "the recipient requested information, a quote, a booking, a contract, or a follow-up",
              ]} />
              <P>Purchased, rented, scraped, or otherwise harvested email lists are strictly prohibited. Uploading a list you did not collect yourself is a material breach of this policy.</P>
            </>
          ),
        },
        {
          id: "email-conduct",
          title: "3. Email Conduct",
          body: (
            <>
              <P>When sending through Banana Cards you must:</P>
              <UL items={[
                "use accurate sender names and reply-to addresses — no impersonation or spoofing",
                "use subject lines that reflect the actual content of the message — no deceptive, misleading or clickbait subjects",
                "identify yourself or your business clearly in the body of the message",
                "include a valid physical address where required by law (e.g. CAN-SPAM, PECR, CASL)",
                "provide a working, one-click unsubscribe mechanism in every marketing message",
                "honour unsubscribe requests promptly and never re-add an unsubscribed contact",
                "respect the recipient's local time, quiet hours and cultural expectations",
                "keep sending frequency reasonable and aligned with what the recipient expects",
              ]} />
            </>
          ),
        },
        {
          id: "prohibited-content",
          title: "4. Prohibited Content",
          body: (
            <>
              <P>You must not use Banana Cards to send messages containing or promoting:</P>
              <UL items={[
                "unsolicited bulk email (spam) of any kind",
                "fraud, scams, phishing, pretexting, or social engineering",
                "malware, viruses, ransomware, spyware, or malicious links and attachments",
                "adult content, pornography, escort services or sexual services",
                "illegal drugs, controlled substances, or unregulated pharmaceuticals",
                "illegal gambling, unlicensed betting, or fraudulent lotteries",
                "hate speech, harassment, threats, incitement to violence, or discrimination",
                "sale of firearms, weapons, explosives, or other restricted items where prohibited",
                "pyramid schemes, get-rich-quick offers, or deceptive investment schemes",
                "cryptocurrency pump-and-dump, unregistered securities, or unauthorised financial advice",
                "counterfeit goods, pirated software, cracked media, or copyright-infringing content",
                "content that violates the privacy of others, exposes personal data, or dox individuals",
                "any content prohibited by our Terms of Service or Legal & Ethical Use Guidelines",
              ]} />
            </>
          ),
        },
        {
          id: "technical",
          title: "5. Technical Requirements",
          body: (
            <>
              <P>To protect deliverability for every sender on the platform you must:</P>
              <UL items={[
                "authenticate your sending domain with SPF, DKIM and DMARC when using a custom domain",
                "keep bounce rates below 5% and spam-complaint rates below 0.1%",
                "remove hard-bounced addresses from your lists immediately",
                "not attempt to bypass, disable, hide, or remove the unsubscribe link",
                "not attempt to bypass rate limits, throttling, or abuse-prevention controls",
                "not send from disposable, temporary, or newly-registered domains designed to evade filtering",
              ]} />
            </>
          ),
        },
        {
          id: "automations",
          title: "6. Automations and Sequences",
          body: (
            <P>
              Automated sequences (drip campaigns, follow-up flows, review requests, welcome series) must respect the same consent, conduct and content rules as manually sent messages. You are responsible for the messages your automations send on your behalf — including automations you configure in the AI Brain, CRM, Bookings, Contracts, or Reviews modules.
            </P>
          ),
        },
        {
          id: "reporting",
          title: "7. Reporting Abuse",
          body: (
            <P>
              If you received a message sent through Banana Cards that you believe violates this policy, please report it to <a href="mailto:abuse@banana.cards" className="font-bold underline-offset-4 hover:underline">abuse@banana.cards</a>. Include the full message with headers where possible. We investigate every report.
            </P>
          ),
        },
        {
          id: "enforcement",
          title: "8. Enforcement",
          body: (
            <>
              <P>Where we reasonably believe this policy has been breached we may, without prior notice:</P>
              <UL items={[
                "throttle, delay, quarantine, or refuse to deliver messages",
                "disable individual campaigns, automations or sender identities",
                "suspend or terminate the account responsible",
                "remove or disable custom domains and authentication records",
                "withhold data exports or restrict access pending investigation",
                "report the account to blocklists, mailbox providers, payment providers, or competent authorities where required or permitted by law",
              ]} />
              <P>Enforcement decisions are made in good faith to protect recipients, deliverability, and the integrity of the platform. Repeated or severe breaches will result in permanent termination without refund.</P>
            </>
          ),
        },
        {
          id: "changes",
          title: "9. Changes to this Policy",
          body: (
            <P>
              We may update this policy from time to time to reflect changes in law, mailbox-provider requirements, or platform features. Material changes will be announced in the product or by email. Continued use of Banana Cards after an update constitutes acceptance of the revised policy.
            </P>
          ),
        },
      ]}
    />
  );
}